import { Image } from 'expo-image';
import * as React from 'react';
import { ActivityIndicator, ScrollView, useWindowDimensions, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { BrandLabel } from '@/components/brand/brand-label';
import { getApiUrl } from '@/lib/api-url';
import { brandAssets } from '@/lib/brand-assets';
import { BRAND } from '@sixam/config/brand';
import { normalizeEmail } from '@sixam/shared/contact-core';
import { Button } from '@sixam/ui/button';
import { Input } from '@sixam/ui/input';
import { Label } from '@sixam/ui/label';
import { Text } from '@sixam/ui/text';

type InvitePreview = {
  discountCode: string | null;
  email: string | null;
  name: string;
  onList: boolean;
  percentOff: number;
};

type SuccessState = {
  discountCode: string | null;
  name: string | null;
  onList: true;
  percentOff: number | null;
};

type StoryState = 'generic' | 'loading' | 'personalized' | 'success' | 'error';

function firstName(name: string | null | undefined) {
  return name?.trim().split(/\s+/)[0] || null;
}

async function readPayload<T>(response: Response, fallbackMessage: string): Promise<T> {
  const payload = (await response.json().catch(() => null)) as (T & { error?: string }) | null;
  if (!response.ok) {
    throw new Error(payload?.error ?? fallbackMessage);
  }
  return payload as T;
}

function DecorativeRoute() {
  return (
    <View
      aria-hidden
      className="absolute bottom-0 right-0 top-0 w-[38%] overflow-hidden border-l border-white/10"
    >
      <View className="absolute -right-24 top-[14%] h-64 w-64 rotate-45 border border-white/10" />
      <View className="absolute -right-6 top-[31%] h-44 w-44 rotate-45 border border-primary" />
      <View className="absolute bottom-[18%] right-[22%] h-3 w-3 rotate-45 bg-primary" />
      <View className="absolute bottom-0 right-[22%] top-[55%] w-px bg-white/15" />
    </View>
  );
}

function OfferSummary({
  discountCode,
  percentOff,
}: Readonly<{ discountCode: string | null; percentOff: number | null }>) {
  if (!percentOff) {
    return null;
  }

  return (
    <View className="gap-3 border-t border-white/15 pt-5">
      <BrandLabel className="text-white/75">Your mailer perk</BrandLabel>
      <View className="flex-row items-end gap-3">
        <Text
          className="font-display text-5xl font-bold leading-none text-primary"
          style={{ fontWeight: '800' }}
        >
          {percentOff}%
        </Text>
        <Text className="pb-1 font-sans text-sm font-bold uppercase tracking-widest text-white">
          off one future event
        </Text>
      </View>
      {discountCode ? (
        <View className="self-start rounded-lg border border-white/20 bg-white/10 px-4 py-3">
          <Text className="font-mono text-sm font-bold uppercase tracking-widest text-white">
            {discountCode}
          </Text>
        </View>
      ) : (
        <Text className="max-w-[360px] text-sm leading-5 text-white/75">
          Join the list to reveal the personal code included with your invitation.
        </Text>
      )}
    </View>
  );
}

function EarlyAccessForm({
  defaultEmail,
  defaultName,
  initialSuccess,
  inviteToken,
  storyState,
}: Readonly<{
  defaultEmail?: string;
  defaultName?: string;
  initialSuccess?: SuccessState | null;
  inviteToken?: string;
  storyState?: StoryState;
}>) {
  const [email, setEmail] = React.useState(defaultEmail ?? '');
  const [error, setError] = React.useState<string | null>(
    storyState === 'error' ? 'Add a valid email before continuing.' : null
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState<SuccessState | null>(
    storyState === 'success'
      ? {
          discountCode: 'KTT-VIBE-6AM',
          name: defaultName ?? 'Saowakon',
          onList: true,
          percentOff: 50,
        }
      : (initialSuccess ?? null)
  );

  async function submit() {
    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail) {
      setError('Add a valid email before continuing.');
      return;
    }

    setError(null);
    setIsSubmitting(true);
    try {
      const response = await fetch(getApiUrl('/api/early-access/subscribe'), {
        body: JSON.stringify({
          email: normalizedEmail,
          name: defaultName,
          token: inviteToken,
        }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      });
      setSuccess(await readPayload<SuccessState>(response, 'Could not join early access.'));
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Could not join early access.');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (success) {
    return (
      <View className="gap-6" accessibilityLiveRegion="polite">
        <View className="gap-3">
          <View className="h-3 w-3 rotate-45 bg-primary" />
          <BrandLabel className="text-primary">You are on the list</BrandLabel>
          <Text
            variant="h2"
            className="normal-case text-white"
            style={{ fontSize: 36, lineHeight: 36 }}
          >
            We cannot wait to run together.
          </Text>
          <Text className="max-w-[420px] text-base leading-6 text-white/80">
            We will send the good stuff to {email}. Early drops, fresh routes, and the next big 6am
            BKK adventure are coming your way.
          </Text>
        </View>
        <OfferSummary discountCode={success.discountCode} percentOff={success.percentOff} />
      </View>
    );
  }

  return (
    <View className="gap-6">
      <View className="gap-2">
        <BrandLabel className="text-primary">Be first out</BrandLabel>
        <Text variant="h3" className="text-white">
          Get the next drop.
        </Text>
        <Text className="max-w-[400px] text-sm leading-5 text-white/80">
          One email when there is something worth showing up for. No noise.
        </Text>
      </View>

      <View className="gap-2">
        <Label className="font-sans text-xs font-bold uppercase tracking-widest text-white/75">
          Email
        </Label>
        <Input
          aria-invalid={Boolean(error)}
          autoCapitalize="none"
          autoComplete="email"
          editable={!isSubmitting}
          keyboardType="email-address"
          onChangeText={setEmail}
          onSubmitEditing={submit}
          placeholder="runner@email.com"
          returnKeyType="go"
          textContentType="emailAddress"
          value={email}
        />
      </View>

      {error ? (
        <Text className="text-sm text-destructive" accessibilityLiveRegion="polite">
          {error}
        </Text>
      ) : null}

      <Button className="h-14 w-full" disabled={isSubmitting} onPress={submit}>
        <Text>{isSubmitting ? 'Joining...' : 'Give me updates'}</Text>
      </Button>

      <Text className="text-xs leading-4 text-white/70">
        By joining, you agree to receive 6am BKK event and product updates. Unsubscribe anytime.
      </Text>
    </View>
  );
}

function storyPreview(storyState?: StoryState): InvitePreview | null {
  if (storyState !== 'personalized' && storyState !== 'success') {
    return null;
  }

  return {
    discountCode: storyState === 'success' ? 'KTT-VIBE-6AM' : null,
    email: 'runner@6ambkk.com',
    name: 'Saowakon',
    onList: storyState === 'success',
    percentOff: 50,
  };
}

function useInvitePreview(inviteToken?: string, storyState?: StoryState) {
  const [result, setResult] = React.useState<{
    error: string | null;
    isLoading: boolean;
    preview: InvitePreview | null;
  }>({
    error: null,
    isLoading: storyState === 'loading' || Boolean(inviteToken),
    preview: storyPreview(storyState),
  });

  React.useEffect(() => {
    if (!inviteToken || storyState) {
      return;
    }

    let active = true;
    fetch(getApiUrl(`/api/early-access/invite?token=${encodeURIComponent(inviteToken)}`))
      .then((response) => readPayload<InvitePreview>(response, 'Could not open this invitation.'))
      .then((preview) => {
        if (active) {
          setResult({ error: null, isLoading: false, preview });
        }
      })
      .catch(() => {
        if (active) {
          setResult({
            error:
              'This personal invitation could not be opened, but you can still join early access.',
            isLoading: false,
            preview: null,
          });
        }
      });

    return () => {
      active = false;
    };
  }, [inviteToken, storyState]);

  return result;
}

function heroCopy(personalName: string | null) {
  if (personalName) {
    return {
      body: 'Thank you for bringing your energy to the Karaoke Tuk Tuk Run. We learned a lot from our first big event, and we are putting every lesson into what comes next.',
      label: `Hey ${personalName}`,
      title: 'Your next adventure starts here.',
    };
  }

  return {
    body: 'We are building a simpler way to discover the right run, understand the run club, and show up ready. Made in Bangkok for people who want to move together.',
    label: 'Early access',
    title: 'Find your run. Meet your people.',
  };
}

function heroType(width: number) {
  if (width >= 1100) {
    return { fontSize: 68, lineHeight: 64 };
  }
  if (width >= 600) {
    return { fontSize: 52, lineHeight: 50 };
  }
  return { fontSize: 40, lineHeight: 39 };
}

function successFromPreview(preview: InvitePreview | null): SuccessState | null {
  if (!preview?.onList) {
    return null;
  }

  return {
    discountCode: preview.discountCode,
    name: preview.name,
    onList: true,
    percentOff: preview.percentOff,
  };
}

function EarlyAccessScreenContent({
  inviteToken,
  storyState,
}: Readonly<{ inviteToken?: string; storyState?: StoryState }>) {
  const { width } = useWindowDimensions();
  const { error: inviteError, isLoading, preview } = useInvitePreview(inviteToken, storyState);
  const personalName = firstName(preview?.name);
  const splitLayout = width >= 860;
  const initialSuccess = successFromPreview(preview);
  const copy = heroCopy(personalName);
  const titleStyle = heroType(width);

  return (
    <SafeAreaView className="flex-1 bg-[#1A1A1A]">
      <DecorativeRoute />
      <ScrollView
        className="flex-1"
        contentContainerStyle={{
          flexGrow: 1,
          paddingHorizontal: Math.min(Math.max(width * 0.055, 20), 72),
          paddingVertical: width >= 860 ? 48 : 28,
        }}
      >
        <View className="mx-auto w-full max-w-[1180px] flex-1">
          <Image
            accessibilityLabel="6am BKK"
            contentFit="contain"
            source={brandAssets.logoReverse}
            style={{ height: 45, width: 208 }}
          />

          <View
            className={splitLayout ? 'flex-1 flex-row items-center gap-16' : 'flex-1 gap-12 pt-16'}
          >
            <View className="min-w-0 flex-1 gap-8">
              <View className="gap-5">
                <BrandLabel className="text-primary">{copy.label}</BrandLabel>
                <Text
                  variant="h1"
                  className="max-w-[720px] text-left text-white"
                  style={titleStyle}
                >
                  {copy.title}
                </Text>
                <Text className="max-w-[610px] text-lg leading-7 text-white/80">{copy.body}</Text>
              </View>

              {preview && !preview.onList ? (
                <OfferSummary discountCode={null} percentOff={preview.percentOff} />
              ) : null}

              <View className="flex-row flex-wrap gap-x-8 gap-y-3 border-t border-white/15 pt-5">
                {['Bangkok made', 'Run club ready', 'Good people first'].map((item) => (
                  <Text
                    key={item}
                    className="font-mono text-xs font-bold uppercase tracking-widest text-white/70"
                  >
                    {item}
                  </Text>
                ))}
              </View>
            </View>

            <View
              className={
                splitLayout
                  ? 'w-[390px] shrink-0 border-l border-white/15 pl-10'
                  : 'w-full max-w-[520px] border-t border-white/15 pt-10'
              }
            >
              {isLoading ? (
                <View className="min-h-[300px] items-center justify-center gap-4">
                  <ActivityIndicator color={BRAND.color.orange} size="large" />
                  <BrandLabel>Opening your invitation</BrandLabel>
                </View>
              ) : (
                <View className="gap-5">
                  {inviteError ? (
                    <View className="border-l-2 border-primary pl-4">
                      <Text className="text-sm leading-5 text-white/80">{inviteError}</Text>
                    </View>
                  ) : null}
                  <EarlyAccessForm
                    defaultEmail={preview?.email ?? undefined}
                    defaultName={preview?.name}
                    initialSuccess={initialSuccess}
                    inviteToken={preview ? inviteToken : undefined}
                    storyState={storyState}
                  />
                </View>
              )}
            </View>
          </View>

          <View className="mt-12 flex-row items-center justify-between border-t border-white/10 pt-5">
            <Text className="font-mono text-xs uppercase tracking-widest text-white/60">
              6am BKK
            </Text>
            <Text className="font-mono text-xs uppercase tracking-widest text-white/60">
              Bangkok run club
            </Text>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

function EarlyAccessScreen(props: Readonly<{ inviteToken?: string; storyState?: StoryState }>) {
  return (
    <EarlyAccessScreenContent
      key={`${props.inviteToken ?? 'generic'}:${props.storyState ?? 'live'}`}
      {...props}
    />
  );
}

export { EarlyAccessScreen };
export type { StoryState };
